import type { Dispatch } from '@reduxjs/toolkit';

import { STEP_NAMES } from '../constants/steps';
import type { CopilotState } from '../data-layer/types';
import {
  addCompletedStep,
  addMessage,
  addVisitedStep,
  removeMessagesBySubType,
  removeSkippedStep,
  setActiveStep,
  setEndpointKey,
} from '../data-store/copilotSlice';

import { showAccountForm } from './stepFormUtils';
import { safeTimeout } from './timeoutUtils';

interface StepTransition {
  nextStep?: keyof typeof STEP_NAMES;
  currentStep?: keyof typeof STEP_NAMES;
  message?: string;
  delay?: number;
}

/**
 * Helper function to show appropriate form based on step type
 */
const showAppropriateForm = (
  dispatch: Dispatch,
  stepName: keyof typeof STEP_NAMES
): void => {
  if (stepName === 'CREATE_ACCOUNT') {
    showAccountForm(
      dispatch,
      'CREATE_ACCOUNT',
      STEP_NAMES.CREATE_ACCOUNT,
      false,
      {}
    );
  } else if (stepName === 'UPDATE_ACCOUNT') {
    showAccountForm(
      dispatch,
      'UPDATE_ACCOUNT',
      STEP_NAMES.UPDATE_ACCOUNT,
      false,
      {}
    );
  } else if (stepName === 'ENABLE_ACCOUNT') {
    showAccountForm(
      dispatch,
      'ENABLE_ACCOUNT',
      STEP_NAMES.ENABLE_ACCOUNT,
      false,
      {}
    );
  } else if (stepName === 'DISABLE_ACCOUNT') {
    showAccountForm(
      dispatch,
      'DISABLE_ACCOUNT',
      STEP_NAMES.DISABLE_ACCOUNT,
      false,
      {}
    );
  } else if (stepName === 'REMOVE_ACCOUNT') {
    showAccountForm(
      dispatch,
      'REMOVE_ACCOUNT',
      STEP_NAMES.REMOVE_ACCOUNT,
      false,
      {}
    );
  } else if (stepName === 'ADD_ACCESS') {
    // Show AddAccess form similar to V1 controller approach
    dispatch(
      addMessage({
        id: 'form-add-access',
        type: 'step_form',
        subType: 'add_access_form',
        timestamp: Date.now(),
        heading: 'Add Access',
        fields: [],
        formData: {},
        isCollapseable: false,
        buttonLabel: 'Continue',
        stepName: 'AddAccess',
      })
    );
  }
  // Add more step types as needed
};

export const createSuccessCallback =
  (dispatch: Dispatch, getState: () => unknown, transition?: StepTransition) =>
  (result: unknown) => {
    // Handle endpointKey if present
    const resultData = result as {
      endpointKey?: number;
      data?: { endpointKey?: number };
    };
    const endpointKey = resultData.endpointKey ?? resultData.data?.endpointKey;

    if (endpointKey) {
      dispatch(setEndpointKey(endpointKey));
    }

    // Navigate to next step if configured
    if (transition?.nextStep) {
      const state = getState() as { copilot?: CopilotState };
      const hasVisitedStep = state.copilot?.navigation.visitedSteps.includes(
        STEP_NAMES[transition.nextStep]
      );

      // Mark current step as completed
      if (transition.currentStep) {
        dispatch(addCompletedStep(STEP_NAMES[transition.currentStep]));
        dispatch(removeSkippedStep(STEP_NAMES[transition.currentStep]));
      }

      if (hasVisitedStep) {
        // Show success message when step was already visited
        dispatch(
          addMessage({
            id: `success-${Date.now()}`,
            type: 'ai_response',
            subType: 'success',
            text: 'The account configuration has been successfully updated.',
            timestamp: Date.now(),
          })
        );
        // Do nothing else - don't navigate if step was already visited
      } else {
        // First time - show transition message
        if (transition.message) {
          dispatch(
            addMessage({
              id: `moving-to-${transition.nextStep.toLowerCase()}-${Date.now()}`,
              type: 'ai_response',
              subType: 'movingToNextStep',
              text: transition.message,
              timestamp: Date.now(),
              loader: true,
            })
          );
        }

        // Navigate to next step after delay
        safeTimeout(() => {
          dispatch(removeMessagesBySubType('movingToNextStep'));
          dispatch(addVisitedStep(STEP_NAMES[transition.nextStep!]));
          dispatch(setActiveStep(STEP_NAMES[transition.nextStep!]));
          showAppropriateForm(dispatch, transition.nextStep!);
        }, transition.delay ?? 2000);
      }
    }
  };

export const createErrorCallback =
  (dispatch: Dispatch, stepName: string) => (message: string) => {
    dispatch(
      addMessage({
        id: `${stepName.toLowerCase()}-error-${Date.now()}`,
        type: 'ai_response',
        subType: 'error',
        text:
          `${stepName} failed. Please check your configuration or try later...` ||
          message,
        timestamp: Date.now(),
      })
    );
  };
